import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Film, Calendar, User, Layers, Play, ChevronDown, ChevronRight,
  Download, Loader2, Folder, Image as ImageIcon, FileText, Music,
  AlertTriangle, CheckCircle2, Clock, Eye, UserPlus, X
} from 'lucide-react';

/**
 * AnimatorAllotmentPage — single-source-of-truth sheet for an animator's work.
 *
 * Founder spec (Feb 12, 2026 — reference: physical Vawworld Animation
 * Allotment Sheet PDF, navy-blue table header, yellow accent badge):
 *
 *   1. Master animatic (client-approved) plays at the top so the artist
 *      can scrub the full sequence while studying their allotment.
 *   2. Header block with Project / Episode / Sequence / Supervisor / PM /
 *      Animator / Start / Est. End dates — matches the printed PDF.
 *   3. Shot table: one row per CBA clip allotted to THIS animator.
 *      Columns: Shot No · Description · Frames From-To-Total · Secs ·
 *      FPS · Type of Work · Priority · Status · Allotted By · Remarks.
 *   4. Expanding a row reveals Raw Materials (Layout file, Character rigs,
 *      Audio stem, Art-Director references). No download buttons for the
 *      shot clip itself — studio data stays in the studio (founder rule).
 *
 * Same animator owns Layout FIRST then Animation for each shot, so the
 * "Type of Work" column shows "Layout", "Animation", or "Layout + Animation"
 * depending on what's assigned.
 */
export default function AnimatorAllotmentPage() {
  const { api, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projectId, setProjectId] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [materials, setMaterials] = useState({}); // cba_clip_id -> materials
  const [matLoading, setMatLoading] = useState(null);
  const [startingId, setStartingId] = useState(null);

  // Founder spec May 13, 2026 Phase 2: when the caller is a vendor
  // producer, we load their team so each row can render an "Assign to
  // teammate" dropdown. Empty/null when caller isn't a producer — the
  // sub-allotment UI is hidden in that case.
  const isProducer = user?.role === 'vendor_producer' || user?.role === 'master_admin';
  const [team, setTeam] = useState([]);
  const [assigningClipId, setAssigningClipId] = useState(null);
  const [pickerOpenClipId, setPickerOpenClipId] = useState(null);
  const loadTeam = useCallback(async () => {
    if (!isProducer) return;
    try {
      const r = await api.get('/animator/allotment/team');
      setTeam(r.data || []);
    } catch {
      setTeam([]);
    }
  }, [api, isProducer]);
  useEffect(() => { loadTeam(); }, [loadTeam]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = projectId ? `/animator/allotment?project_id=${projectId}` : '/animator/allotment';
      const r = await api.get(url);
      setData(r.data);
      if (!projectId && r.data.current_project_id) {
        setProjectId(r.data.current_project_id);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('allotment load failed', e);
    } finally {
      setLoading(false);
    }
  }, [api, projectId]);

  useEffect(() => { load(); }, [load]);

  const assignToTeamMember = async (clipId, memberUserId, stage = 'both') => {
    setAssigningClipId(clipId);
    try {
      await api.post(
        `/animator/allotment/shot/${clipId}/assign-team-member?project_id=${data.current_project_id}`,
        { member_user_id: memberUserId, stage },
      );
      setPickerOpenClipId(null);
      await load();
      await loadTeam();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('assign failed', e);
      alert(e.response?.data?.detail || 'Assign failed');
    } finally {
      setAssigningClipId(null);
    }
  };

  const reclaimShot = async (clipId) => {
    if (!window.confirm('Reclaim this shot from the team member?')) return;
    setAssigningClipId(clipId);
    try {
      await api.post(
        `/animator/allotment/shot/${clipId}/unassign?project_id=${data.current_project_id}`,
        {},
      );
      await load();
      await loadTeam();
    } catch (e) {
      alert(e.response?.data?.detail || 'Reclaim failed');
    } finally {
      setAssigningClipId(null);
    }
  };

  const toggleRow = async (clipId) => {
    if (expandedRow === clipId) { setExpandedRow(null); return; }
    setExpandedRow(clipId);
    if (!materials[clipId]) {
      setMatLoading(clipId);
      try {
        const r = await api.get(`/animator/allotment/shot/${clipId}/materials?project_id=${data.current_project_id}`);
        setMaterials(prev => ({ ...prev, [clipId]: r.data }));
      } catch (e) {
        setMaterials(prev => ({ ...prev, [clipId]: { error: e.response?.data?.detail || 'Failed to load materials' } }));
      } finally {
        setMatLoading(null);
      }
    }
  };

  const startWork = async (clipId) => {
    setStartingId(clipId);
    try {
      await api.post(`/animator/allotment/shot/${clipId}/start?project_id=${data.current_project_id}`);
      await load();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('start failed', e);
    } finally {
      setStartingId(null);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="allotment-loading">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  if (!data || !data.projects.length) {
    return (
      <div className="max-w-3xl mx-auto py-16 text-center" data-testid="allotment-empty">
        <Layers className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-zinc-300">No Allotment Yet</h2>
        <p className="text-sm text-zinc-500 mt-2">
          You don&apos;t have any Layout or Animation shots assigned yet.
          Once your supervisor allots shots to you, they&apos;ll appear here.
        </p>
      </div>
    );
  }

  const { header, animatic, shots, summary, projects } = data;

  return (
    <div className="space-y-5" data-testid="animator-allotment-page">
      {/* ── HEADER STRIP ─────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white tracking-tight">
            ANIMATION ALLOTMENT SHEET
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Your assigned shots, the approved animatic, and the raw materials you need.
          </p>
        </div>
        {projects.length > 1 && (
          <select
            value={projectId || ''}
            onChange={(e) => { setProjectId(e.target.value); setExpandedRow(null); setMaterials({}); }}
            className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-amber-400"
            data-testid="allotment-project-picker"
          >
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name} · {p.task_count} shots</option>
            ))}
          </select>
        )}
      </div>

      {/* ── PROJECT INFO CARD (matches PDF top block) ───────────────── */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="allotment-header-card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2 text-sm">
          <KV k="PROJECT NAME" v={header.project_name} />
          <KV k="SUPERVISOR" v={header.supervisor} />
          <KV k="ANIMATOR" v={header.animator} highlight />
          <KV k="EPISODE" v={header.episode} />
          <KV k="PRODUCTION MANAGER" v={header.production_manager} />
          <KV k="START DATE" v={formatDate(header.start_date)} />
          <KV k="SEQUENCE" v={header.sequence} />
          <KV k="DATE" v={formatDate(header.date)} />
          <KV k="EST. END DATE" v={formatDate(header.est_end_date)} />
        </div>
      </div>

      {/* ── ANIMATIC PLAYER ─────────────────────────────────────────── */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden" data-testid="allotment-animatic-card">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between bg-gradient-to-r from-amber-500/10 to-transparent">
          <div className="flex items-center gap-2">
            <Film className="w-4 h-4 text-amber-400" />
            <h2 className="text-sm font-semibold text-white">
              Client-Approved Animatic
              {animatic.round ? <span className="ml-2 text-[10px] font-mono text-amber-400/80">v{String(animatic.round).padStart(2, '0')}</span> : null}
            </h2>
          </div>
          {animatic.submitted_at && (
            <span className="text-[10px] text-zinc-500 font-mono">Approved: {formatDate(animatic.submitted_at)}</span>
          )}
        </div>
        {animatic.cloud_url ? (
          <div className="p-4 space-y-3">
            <video
              src={animatic.cloud_url}
              controls
              preload="metadata"
              className="w-full max-h-[60vh] bg-black rounded-md"
              data-testid="allotment-animatic-player"
            />
            <div className="flex items-center justify-between flex-wrap gap-2 text-[10px] text-zinc-500">
              <span className="font-mono">{animatic.filename || '—'}</span>
              {animatic.nas_path && (
                <span className="font-mono text-zinc-600 truncate" title={animatic.nas_path}>
                  NAS: {animatic.nas_path}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="p-6 text-center text-xs text-zinc-500" data-testid="allotment-animatic-empty">
            <AlertTriangle className="w-6 h-6 text-zinc-700 mx-auto mb-2" />
            Animatic hasn&apos;t been client-approved yet for this project.
          </div>
        )}
      </div>

      {/* ── SHOT TABLE ─────────────────────────────────────────────── */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden" data-testid="allotment-table-card">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-blue-400" />
            <h2 className="text-sm font-semibold text-white">Your Allotted Shots</h2>
          </div>
          <span className="text-[10px] text-zinc-500 font-mono">
            {summary.total_shots} shots · {summary.total_frames} frames · {summary.total_duration_sec.toFixed(2)}s
          </span>
        </div>

        {shots.length === 0 ? (
          <div className="p-12 text-center" data-testid="allotment-no-shots">
            <Layers className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">No shots assigned to you on this project yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="allotment-table">
              <thead>
                <tr className="bg-[#0d1430] text-white text-[10px] font-bold uppercase tracking-wider">
                  <th className="px-3 py-2 text-left w-8"></th>
                  <th className="px-3 py-2 text-left">Shot Number</th>
                  <th className="px-3 py-2 text-left">Shot Description</th>
                  <th className="px-3 py-2 text-center">Frames From</th>
                  <th className="px-3 py-2 text-center">Frames To</th>
                  <th className="px-3 py-2 text-center">Total</th>
                  <th className="px-3 py-2 text-center">Secs</th>
                  <th className="px-3 py-2 text-center">FPS</th>
                  <th className="px-3 py-2 text-center">Type of Work</th>
                  <th className="px-3 py-2 text-center">Priority</th>
                  <th className="px-3 py-2 text-center">Status</th>
                  <th className="px-3 py-2 text-left">Allotted By</th>
                  <th className="px-3 py-2 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {shots.map((s, idx) => (
                  <React.Fragment key={s.cba_clip_id}>
                    <tr
                      className={`border-b border-zinc-800/40 hover:bg-zinc-800/30 transition-colors cursor-pointer ${idx % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-900/50'}`}
                      onClick={() => toggleRow(s.cba_clip_id)}
                      data-testid={`allotment-row-${s.cba_clip_id}`}
                    >
                      <td className="px-3 py-2 text-zinc-500">
                        {expandedRow === s.cba_clip_id
                          ? <ChevronDown className="w-3.5 h-3.5" />
                          : <ChevronRight className="w-3.5 h-3.5" />}
                      </td>
                      <td className="px-3 py-2 font-mono text-blue-400 font-bold">{s.shot_number}</td>
                      <td className="px-3 py-2 text-zinc-200">{s.shot_description}</td>
                      <td className="px-3 py-2 text-center font-mono text-zinc-300">{s.frames_from}</td>
                      <td className="px-3 py-2 text-center font-mono text-zinc-300">{s.frames_to}</td>
                      <td className="px-3 py-2 text-center font-mono text-zinc-300">{s.frames_total}</td>
                      <td className="px-3 py-2 text-center font-mono text-zinc-300">{s.duration_sec.toFixed(2)}</td>
                      <td className="px-3 py-2 text-center font-mono text-zinc-400">{Number(s.fps).toFixed(2)}</td>
                      <td className="px-3 py-2 text-center text-zinc-300">{s.type_of_work}</td>
                      <td className="px-3 py-2 text-center"><PriorityPill p={s.priority} /></td>
                      <td className="px-3 py-2 text-center"><StatusPill s={s.status} label={s.status_label} /></td>
                      <td className="px-3 py-2 text-zinc-400">{s.allotted_by}</td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center gap-1 justify-center" onClick={(e) => e.stopPropagation()}>
                          {(s.status === 'not_started' || s.status === 'unassigned') && (
                            <button
                              onClick={() => startWork(s.cba_clip_id)}
                              disabled={startingId === s.cba_clip_id}
                              className="h-6 px-2 bg-emerald-500/20 text-emerald-300 text-[10px] rounded hover:bg-emerald-500/30 flex items-center gap-1 disabled:opacity-50"
                              data-testid={`allotment-start-btn-${s.cba_clip_id}`}
                            >
                              {startingId === s.cba_clip_id
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <Play className="w-3 h-3" />}
                              Start
                            </button>
                          )}
                          {s.current_task_id && (s.status === 'in_progress' || s.status === 'revise') && (
                            // Submit button removed from My Allotment May 24,
                            // 2026 (founder spec): artists submit ONLY via the
                            // inline SUBMIT button on the task row in MY TASKS.
                            // The allotment surface is for shot-discovery &
                            // team assignment, not file uploads.
                            null
                          )}
                          {/* Sub-allotment — only producers see this. */}
                          {isProducer && team.length > 0 && (
                            <div className="relative">
                              <button
                                onClick={() => setPickerOpenClipId(pickerOpenClipId === s.cba_clip_id ? null : s.cba_clip_id)}
                                disabled={assigningClipId === s.cba_clip_id}
                                data-testid={`allotment-assign-btn-${s.cba_clip_id}`}
                                className="h-6 px-2 bg-amber-500/20 text-amber-300 text-[10px] rounded hover:bg-amber-500/30 flex items-center gap-1 disabled:opacity-50"
                                title="Assign this shot to a team member"
                              >
                                {assigningClipId === s.cba_clip_id
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : <UserPlus className="w-3 h-3" />}
                                Assign
                              </button>
                              {pickerOpenClipId === s.cba_clip_id && (
                                <div
                                  className="absolute right-0 top-full mt-1 w-64 bg-zinc-950 border border-amber-500/30 rounded-md shadow-2xl z-30"
                                  data-testid={`allotment-team-picker-${s.cba_clip_id}`}
                                >
                                  <div className="px-3 py-1.5 border-b border-zinc-800 flex items-center justify-between">
                                    <span className="text-[9px] font-bold uppercase tracking-widest text-amber-300">Assign to teammate</span>
                                    <button onClick={() => setPickerOpenClipId(null)} className="text-zinc-500 hover:text-zinc-300">
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                  <ul className="max-h-64 overflow-y-auto">
                                    {team.map(m => (
                                      <li key={m.id}>
                                        <button
                                          onClick={() => assignToTeamMember(s.cba_clip_id, m.id, 'both')}
                                          data-testid={`allotment-team-pick-${m.id}`}
                                          className="w-full px-3 py-2 hover:bg-zinc-800 flex items-center justify-between gap-2 text-left"
                                        >
                                          <div className="min-w-0">
                                            <p className="text-[11px] text-zinc-200 truncate">{m.full_name || m.email}</p>
                                            <p className="text-[9px] text-zinc-600">
                                              {m.tasks_in_progress} active · {m.tasks_completed} done
                                            </p>
                                          </div>
                                          <UserPlus className="w-3 h-3 text-amber-400 shrink-0" />
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                          {/* Reclaim — producer pulls a shot back from any
                              team member. Backend safely no-ops when nothing
                              to reclaim, so we can always show this when team
                              exists. */}
                          {isProducer && team.length > 0 && (
                            <button
                              onClick={() => reclaimShot(s.cba_clip_id)}
                              data-testid={`allotment-reclaim-btn-${s.cba_clip_id}`}
                              className="h-6 px-2 bg-red-500/15 text-red-300 text-[10px] rounded hover:bg-red-500/25 flex items-center gap-1"
                              title="Reclaim this shot from team"
                            >
                              <X className="w-3 h-3" /> Reclaim
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedRow === s.cba_clip_id && (
                      <tr className="bg-black/40">
                        <td colSpan={13} className="px-6 py-4" data-testid={`allotment-materials-${s.cba_clip_id}`}>
                          <MaterialsDrawer
                            mats={materials[s.cba_clip_id]}
                            loading={matLoading === s.cba_clip_id}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-zinc-950 text-zinc-300 font-mono text-[11px] border-t-2 border-zinc-700">
                  <td colSpan={3} className="px-3 py-3 font-bold">TOTAL SHOTS: <span className="text-amber-400">{summary.total_shots}</span></td>
                  <td colSpan={3} className="px-3 py-3 text-center font-bold">TOTAL FRAMES: <span className="text-amber-400">{summary.total_frames}</span></td>
                  <td colSpan={3} className="px-3 py-3 text-center font-bold">DURATION: <span className="text-amber-400">{summary.total_duration_sec.toFixed(2)} SECS</span></td>
                  <td colSpan={4} className="px-3 py-3 text-right text-zinc-600">
                    Animator: <span className="text-zinc-400">{header.animator}</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────── helper components ─────────────────────────── */

function KV({ k, v, highlight }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 min-w-[140px]">{k}</span>
      <span className={`text-sm ${highlight ? 'text-amber-400 font-semibold' : 'text-zinc-200'}`}>: {v || '—'}</span>
    </div>
  );
}

function PriorityPill({ p }) {
  const style = {
    high: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
    medium: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    low: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  }[p] || 'bg-zinc-700/40 text-zinc-300';
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold capitalize border ${style}`}>{p}</span>;
}

function StatusPill({ s, label }) {
  const style = {
    not_started: 'bg-zinc-700/40 text-zinc-300',
    unassigned: 'bg-zinc-700/40 text-zinc-300',
    blocked: 'bg-red-500/20 text-red-300',
    in_progress: 'bg-blue-500/20 text-blue-300',
    review: 'bg-violet-500/20 text-violet-300',
    revise: 'bg-orange-500/20 text-orange-300',
    completed: 'bg-emerald-500/20 text-emerald-300',
    approved: 'bg-emerald-500/20 text-emerald-300',
    client_approved: 'bg-emerald-500/20 text-emerald-300',
  }[s] || 'bg-zinc-700/40 text-zinc-300';
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${style}`}>{label}</span>;
}

function MaterialsDrawer({ mats, loading }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-zinc-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading raw materials...
      </div>
    );
  }
  if (!mats) return null;
  if (mats.error) {
    return <div className="text-xs text-red-400 flex items-center gap-2"><AlertTriangle className="w-3 h-3" /> {mats.error}</div>;
  }

  const sections = [
    { title: 'Layout File (start here)', icon: FileText, items: mats.layout_files, kind: 'layout', emptyHint: 'Layout not yet approved — you will work on Layout first, then Animation.' },
    { title: 'Character Rigs', icon: User, items: mats.rigs, kind: 'rig', emptyHint: 'No approved character rigs found for this shot.' },
    { title: 'Audio Stem (from CBA)', icon: Music, items: mats.audio, kind: 'audio', emptyHint: 'Audio extraction pending — CBA must be locked + extracted first.' },
    { title: 'Art-Director References', icon: ImageIcon, items: mats.references, kind: 'ref', emptyHint: 'No references uploaded for this project.' },
  ];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {sections.map(sec => (
          <div key={sec.title} className="bg-zinc-900 border border-zinc-800 rounded p-3">
            <div className="flex items-center gap-2 mb-2">
              <sec.icon className="w-3.5 h-3.5 text-amber-400" />
              <h3 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-300">{sec.title}</h3>
              <span className="text-[9px] text-zinc-600 ml-auto">{(sec.items || []).length}</span>
            </div>
            {(sec.items || []).length === 0 ? (
              <p className="text-[10px] text-zinc-600 italic">{sec.emptyHint}</p>
            ) : (
              <ul className="space-y-1.5">
                {sec.items.map((it, i) => (
                  <li key={`${sec.kind}-${i}`} className="flex items-center gap-2 text-[10px]">
                    <Folder className="w-3 h-3 text-zinc-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-200 truncate" title={it.filename || it.title}>
                        {it.filename || it.title || '—'}
                      </p>
                      {it.nas_path && (
                        <p className="text-zinc-600 font-mono truncate" title={it.nas_path}>
                          {it.nas_path}
                        </p>
                      )}
                      {it.asset_code && (
                        <span className="text-amber-400/80 font-mono">{it.asset_code}</span>
                      )}
                    </div>
                    {it.cloud_url && sec.kind !== 'audio' && (
                      <a
                        href={it.cloud_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 shrink-0"
                        title="Open in browser (preview only — work files stay on NAS)"
                      >
                        <Eye className="w-3 h-3" />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
      <p className="text-[10px] text-zinc-600 italic flex items-center gap-1">
        <CheckCircle2 className="w-3 h-3 text-emerald-400" />
        All work files stay on the studio NAS. Open them from your File Station / mapped drive.
      </p>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}
